import {
  BaseQueryFn,
  createApi,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";

import { persistor, type RootState } from "../index";
import { APP_IDS } from "../../constants/app-ids.constant";
import { logout, setCredentials } from "../slices/authSlice";
import axios, { AxiosError } from "axios";
import { toast } from "react-toastify";
import { flattenErrorMessage, getServerError } from "../../services";
import { ErrorHandlingTypeEnum } from "../../enums";

const ERROR_MESSAGES: Record<string, string> = {
  FETCH_ERROR: "Unable to connect to server",
  TIMEOUT_ERROR: "Server took too long to respond",
  PARSING_ERROR: "Failed to parse server response",
};

const mutex = new Mutex();

export const prepareHeaders = (
  getState: () => unknown,
): Record<string, string> => {
  const state = getState() as RootState;
  const headers: Record<string, string> = {};

  const token = state.auth.token;
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const language = localStorage.getItem("i18nextLng") || "en";
  if (language) {
    headers["Accept-Language"] = language;
  }

  headers["x-platform-key"] =
    APP_IDS[process.env.REACT_APP_ENVIRONMENT as any]?.["web_cp"] || "";

  return headers;
};

const rawAxiosBaseQuery =
  (): BaseQueryFn<
    {
      url: string;
      method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
      data?: any;
      body?: any;
      params?: any;
      onUploadProgress?: (progressEvent: ProgressEvent) => void;
      responseType?: "blob" | "json";
    },
    unknown,
    FetchBaseQueryError
  > =>
  // @ts-ignore
  async (
    { url, method = "GET", data, body, params, onUploadProgress, responseType },
    api,
  ) => {
    try {
      const headers = prepareHeaders(api.getState);
      const requestBody = data !== undefined ? data : body;

      const result = await axios({
        url: `${process.env.REACT_APP_API_URL || "/api"}${url}`,
        method,
        data: requestBody,
        params,
        headers,
        withCredentials: true,
        onUploadProgress,
        ...(responseType && { responseType }),
      });

      return { data: result.data };
    } catch (error) {
      const err = error as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data || err.message,
        },
      };
    }
  };

const axiosBaseQueryWithReauth: BaseQueryFn<
  any,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();

  let result = await rawAxiosBaseQuery()(args, api, extraOptions);
  const requestUrl = typeof args === "string" ? args : String(args?.url || "");
  const isAuthAttempt =
    /\/auth\/(signin|verify|forget-password)(\/|$|\?)/.test(requestUrl);

  if (result.error?.status === 401 && !isAuthAttempt) {
    const refreshToken = (api.getState() as RootState).auth.refreshToken;

    if (!refreshToken) {
      api.dispatch(logout());
      localStorage.removeItem("session");
      return result;
    }

    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const refreshResult = await rawAxiosBaseQuery()(
          {
            url: "/api/auth/refresh-token",
            method: "POST",
            data: { refreshToken },
          },
          api,
          extraOptions,
        );

        if (refreshResult.data) {
          const refreshData = refreshResult.data as any;

          api.dispatch(
            setCredentials({
              token: refreshData.token,
              refreshToken: refreshData.refreshToken,
            }),
          );
          result = await rawAxiosBaseQuery()(args, api, extraOptions);
        } else {
          api.dispatch(logout());
          persistor.purge();
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await rawAxiosBaseQuery()(args, api, extraOptions);
    }
  }
  const statusCode = Number(result.error?.status);
  const showServerMessage =
    [400, 406, 429, 404].includes(statusCode) ||
    (statusCode === 401 && isAuthAttempt);

  if (result.error && showServerMessage) {
    const parsed = getServerError(result.error);

    if (
      [ErrorHandlingTypeEnum.ALERT, ErrorHandlingTypeEnum.UNKNOWN].includes(
        parsed.type,
      )
    ) {
      showErrorToast(parsed.error, 300);
    }

    return { error: parsed } as any;
  }

  if (result.error && [409].includes(result.error.status as number)) {
    const parsed = getServerError(result.error);

    if (
      [ErrorHandlingTypeEnum.ALERT, ErrorHandlingTypeEnum.UNKNOWN].includes(
        parsed.type,
      )
    ) {
      showErrorToast(parsed.error, 300);
    }
    api.dispatch(logout());
    return { error: parsed } as any;
  }

  const errorStatus = result.error?.status;
  if (errorStatus && ERROR_MESSAGES[errorStatus]) {
    showErrorToast(ERROR_MESSAGES[errorStatus], 300);
  }

  return result;
};

const showErrorToast = (message: string, delay = 300) => {
  const text = flattenErrorMessage(message) || "An unexpected error occurred";
  setTimeout(() => toast.error(text), delay);
};

const requestUrlOf = (args: unknown): string => {
  if (typeof args === "string") return args;
  if (args && typeof args === "object" && "url" in args) {
    const url = (args as { url?: unknown }).url;
    return typeof url === "string" ? url : "";
  }
  return "";
};

/**
 * Login and other public auth calls return 401 for bad credentials.
 * Those must surface the server message. Other 401s stay on the refresh path.
 */
const isPublicAuthRequest = (url: string) =>
  /\/auth\/(signin|forget-password|verify)(\/|$|\?)/.test(url);

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: axiosBaseQueryWithReauth,
  tagTypes: [
    "Governorate",
    "City",
    "School",
    "Course",
    "Subject",
    "Testers",
    "TesterFeedback",
    "SubjectNote",
    "Paginated-Subject-Notes",
    "SubjectDocument",
    "Paginated-Subject-Documents",
    "SubjectInstruction",
    "Paginated-Subject-Instructions",
    "Chapter",
    "Chapter-Stats",
    "Chapter-Flash-Card-Stats",
    "Chapter-Lecture-Stats",
    "Chapter-Select-List-By-Subject",
    "Organizing-Chapters",
    "Lesson",
    "Lesson-Detail",
    "Lesson-Select-List-By-Chapter",
    "Question",
    "Questions-By-Lesson",
    "CourseDuration",
    "CourseDurations",
    "Skill",
    "Paginated-Flash-Cards",
    "FlashCard",
    "Calendar",
    "AppSettings",
    "Supplication",
    "Supplications",
    "Banner",
    "Account",
    "Paginated-Teacher-Admins",
    "TeacherAdmin",
    "Paginated-Head-Teacher-Admins",
    "Paginated-System-Admins",
    "HeadTeacherAdmin",
    "SystemAdmin",
    "Paginated-Admins",
    "Admin",
    "Olympic",
    "PromoCodes",
    "PromoCode",
    "Plan",
    "Student",
    "Parent",
    "Paginated-Teachers",
    "User",
    "Analytics",
    "Notification",
    "Lecture",
    "LectureAssignment",
    "Paginated-Lecture-Assignment",
    "Lectures-By-Lesson",
    "AssignmentQuestion",
    "Paginated-Assignment-Questions",
    "Complaint",
    "Transaction-History",
    "Purchase",
    "Report",
    "AppConfig",
    "HomeworkSolution",
    "Subscription",
    "Template",
    "Media",
    "Log",
    "Contact",
    "Contacts",
    "Stage",
    "EducationSystem",
    "Paginated-EducationSystems",
    "EducationSystem-Select-List",
    "Curriculum",
    "Paginated-Curricula",
    "Curriculum-Select-List",
    "EducationalStage",
    "Paginated-EducationalStages",
    "FileManager",
    "Categories",
    "Categories-Select-List",
    "Grade",
    "Grades",
    "Grades-Select-List-By-Course",
    "Plans",
    "Paginated-Subjects",
    "Subjects-By-Grade",
    "Paginated-Students",
    "Paginated-Governorates",
    "Governorate-Select-List",
    "Paginated-Cities",
    "Cities-Select-List",
    "Paginated-Schools",
    "School-Select-List",
    "Paginated-Parents",
    "Paginated-Lecture-Comments",
    "PurchasablePlans",
    "ControlFeatures",
    "ControlFeature",
    "ControlFeature-SelectList",
    "LiveLecture",
    "TeacherSchedule",
  ],
  endpoints: () => ({}),
});
